import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAPI } from '../../Contexts/APIContext'
import { useAuth } from '../../Contexts/AuthContext'
import useAdminUserNames from '../../services/hooks/useAdminUserNames'
import NewRequestCard from '../IngestRequests/NewRequestCard'
import RequestFilters, { applyRequestFilters, FILTER_DEFAULTS } from '../IngestRequests/RequestFilters'
import { statusLabel, statusTone, isSettled, needsAPerson, requesterName } from '../IngestRequests/requestContract'
import useIngestRequest from '../../Components/IngestRequest/useIngestRequest'
import { ModalWindow } from '../../Components/GeneralComponents/ModalWindow/ModalWindow'
import successToast from '../../Components/Toasts/SuccessToast'
import classes from './index.module.css'

/**
 * Ingest Status - every artist synchronization, and the way to start one.
 *
 * Reads GET /ingest/requests, which takes no query and returns the most
 * recent 100, so the filters below narrow in the browser. Polls only while
 * something is actually moving.
 */

const POLL_MS = 5000

function ago(value) {
  if (!value) return '—'
  const then = new Date(value).getTime()
  if (isNaN(then)) return '—'
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86400)}d`
}

/** How long it ran, not how long ago it started. */
function span(from, to) {
  const start = new Date(from).getTime()
  const end = new Date(to).getTime()
  if (isNaN(start) || isNaN(end) || end < start) return '—'
  const seconds = Math.round((end - start) / 1000)
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  return `${Math.floor(seconds / 3600)}h`
}

function RequestRow({ request, currentUser, adminNames }) {
  // Prefer the real artist name once one exists: the committed Feels name,
  // then the name YouTube staged for it, then the raw ids nothing better is
  // on the row for (lyrics-sweep / non-artist rows have neither name).
  const subject =
    request.artist_display_name || request.youtube_name || request.artist_f_id || request.source_id || 'Unknown artist'
  return (
    <tr className={classes.row}>
      <td className={classes.cell}>
        <Link className={classes.requestMain} to={`/ingest/${request.id}`}>
          <span className={classes.subject}>{subject}</span>
        </Link>
        {/* The channel is the one id every row has, and it is worth opening. */}
        <span className={classes.subjectId}>
          {request.youtube_name && `${request.youtube_name} · `}
          <a
            className={classes.subjectIdLink}
            href={`https://music.youtube.com/channel/${request.source_id}`}
            target="_blank"
            rel="noreferrer"
          >
            {request.source_id}
          </a>
        </span>
        {request.force_refresh && <span className={classes.meta}>fresh fetch</span>}
      </td>
      <td className={classes.cell}>
        <span className={`${classes.badge} ${classes['tone_' + statusTone(request)] || ''}`}>
          {statusLabel(request)}
        </span>
        {request.step && <span className={classes.meta}>{request.step}</span>}
      </td>
      <td className={classes.cell} title={request.requested_by || undefined}>
        {requesterName(request.requested_by, { currentUser, adminNames }) || request.requested_by || '—'}
      </td>
      <td className={classes.cell}>
        <span className={classes.waitValue}>{ago(request.date_created)}</span>
        {request.date_finished && (
          <span className={classes.meta}>took {span(request.date_created, request.date_finished)}</span>
        )}
      </td>
    </tr>
  )
}

RequestRow.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.string,
    artist_f_id: PropTypes.string,
    artist_display_name: PropTypes.string,
    youtube_name: PropTypes.string,
    source_id: PropTypes.string,
    status: PropTypes.string,
    step: PropTypes.string,
    requested_by: PropTypes.string,
    force_refresh: PropTypes.bool,
    date_created: PropTypes.string,
    date_finished: PropTypes.string,
  }).isRequired,
  currentUser: PropTypes.object,
  adminNames: PropTypes.instanceOf(Map),
}

export default function IngestStatus() {
  const navigate = useNavigate()
  const { getIngestRequests } = useAPI()
  const { getCurrentUser } = useAuth()
  const currentUser = getCurrentUser()
  const adminNames = useAdminUserNames()
  // The same hook the in-context buttons use, so the resolved channel id -
  // not the raw paste - is what gets sent, and the submit key is managed in
  // one place.
  const { submit, reset: resetSubmit } = useIngestRequest()

  const [requestOpen, setRequestOpen] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [filters, setFilters] = useState(() => ({ ...FILTER_DEFAULTS }))
  const panelRef = useRef(null)

  const query = useQuery({
    queryKey: ['ingest-requests'],
    queryFn: () => getIngestRequests(),
    meta: { suppressGlobalError: true },
    retry: false,
    refetchInterval: q => {
      if (q.state.status === 'error') return false
      const rows = (q.state.data && q.state.data.requests) || []
      // Only rows actually moving. needs_review/needs_attention wait on a
      // person for days, and every poll costs a Temporal sweep server-side.
      return rows.some(r => !isSettled(r.status) && !needsAPerson(r.status)) ? POLL_MS : false
    },
  })

  const rows = useMemo(() => (query.data && query.data.requests) || [], [query.data])
  const shown = useMemo(() => applyRequestFilters(rows, filters), [rows, filters])

  const closeRequest = useCallback(() => {
    setRequestOpen(false)
    setCreateError(null)
    setSubmitting(false)
    resetSubmit()
  }, [resetSubmit])

  useEffect(() => {
    if (!requestOpen) return undefined
    function onKey(e) {
      if (e.key === 'Escape') closeRequest()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [requestOpen, closeRequest])

  useEffect(() => {
    if (requestOpen && panelRef.current) panelRef.current.focus()
  }, [requestOpen])

  const handleRequest = useCallback(
    async (input, opts) => {
      setSubmitting(true)
      setCreateError(null)
      const result = await submit(input, { forceRefresh: opts && opts.forceRefresh })
      setSubmitting(false)
      if (!result.ok) {
        if (result.reason === 'in_flight') return
        setCreateError(result.message || 'We could not start that.')
        return
      }
      setRequestOpen(false)
      resetSubmit()
      // Toast BEFORE navigating: the toast host is app-level and survives the
      // route change, and returning early here used to make `reused`
      // unreachable - so joining someone else's request looked like starting
      // your own.
      successToast(
        result.reused
          ? 'That artist was already being synchronized. This is that request — nothing was started twice.'
          : 'Synchronization started.'
      )
      if (result.id) navigate(`/ingest/${result.id}`)
    },
    [submit, resetSubmit, navigate]
  )

  return (
    <div className={classes.shell}>
      <div className={classes.header}>
        <h1 className={classes.title}>Ingest Status</h1>
        <Link to="/ingest/workers" className={classes.workersLink}>Workers and Mac sign-in</Link>
        <button type="button" className={classes.requestButton} onClick={() => setRequestOpen(true)}>
          <span className={classes.requestPlus} aria-hidden="true">
            +
          </span>
          Request
        </button>
      </div>

      <div className={classes.body}>
        {/* Filters over nothing are furniture. */}
        {rows.length > 0 && (
          <RequestFilters
            status={filters.status}
            q={filters.q}
            onChange={next => setFilters(prev => ({ ...prev, ...next }))}
            counts={{ shown: shown.length, total: rows.length }}
          />
        )}

        {query.isLoading && <p className={classes.empty}>Loading requests…</p>}
        {query.isError && (
          <p className={classes.empty}>
            We could not load requests.{' '}
            <button type="button" className={classes.linkButton} onClick={() => query.refetch()}>
              Try again
            </button>
          </p>
        )}
        {!query.isLoading && !query.isError && rows.length === 0 && (
          <div className={classes.blank}>
            <p className={classes.blankLead}>Nothing has been synced yet.</p>
            <p className={classes.blankBody}>
              Syncing an artist pulls their releases and songs from YouTube into the catalogue. You will
              need their YouTube channel link.
            </p>
            <button type="button" className={classes.requestButton} onClick={() => setRequestOpen(true)}>
              <span className={classes.requestPlus} aria-hidden="true">
                +
              </span>
              Request
            </button>
          </div>
        )}

        {!query.isLoading && !query.isError && rows.length > 0 && shown.length === 0 && (
          <p className={classes.empty}>No requests match this filter.</p>
        )}
        {shown.length > 0 && (
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.head}>Artist</th>
                <th className={classes.head}>Status</th>
                <th className={classes.head}>Asked by</th>
                <th className={classes.head}>Asked</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(request => (
                <RequestRow key={request.id} request={request} currentUser={currentUser} adminNames={adminNames} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ModalWindow open={requestOpen} onClose={closeRequest}>
        <div
          className={classes.requestModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ingest-request-modal-title"
          tabIndex={-1}
          ref={panelRef}
        >
          <h2 className={classes.requestModalTitle} id="ingest-request-modal-title">
            Synchronize an artist
          </h2>
          <NewRequestCard onSubmit={handleRequest} isSubmitting={isSubmitting} error={createError} />
        </div>
      </ModalWindow>
    </div>
  )
}
